import React, { useState } from "react";
import {
    useStripeExpressBrandingQuery,
    useUpdateStripeExpressBrandingMutation,
} from "@api/stripeExpress.api";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Loader } from "@design-system/Loader/Loader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { isErrorToastedByBackendClient } from "@lib/backendClient";
import {
    buildStripeExpressBrandingUpdate,
    draftFromStripeExpressBranding,
    StripeExpressBrandingDraft,
} from "./stripeExpressBrandingDraft";
import { StripeExpressBrandingFields } from "./StripeExpressBrandingFields";
import styles from "./StripeExpressBrandingSection.module.scss";

interface Props {
    connectionId: number;
    isOpened: boolean;
}

export const StripeExpressBrandingSection: React.FC<Props> = ({
    connectionId,
    isOpened,
}) => {
    const toaster = useToaster();
    const brandingQuery = useStripeExpressBrandingQuery({
        connectionId,
        isEnabled: isOpened,
    });
    const updateBranding = useUpdateStripeExpressBrandingMutation(connectionId);
    const [draft, setDraft] = useState<StripeExpressBrandingDraft | null>(null);
    const branding = brandingQuery.data;

    if (branding && draft === null) {
        setDraft(draftFromStripeExpressBranding(branding));
    }

    let body: React.ReactNode = <Loader inline />;
    if (brandingQuery.isError) {
        body = (
            <HStack gap={12} align="center">
                <p className={styles.status}>Couldn't load branding.</p>
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                        void brandingQuery.refetch();
                    }}
                >
                    Try again
                </Button>
            </HStack>
        );
    } else if (branding && draft) {
        body = (
            <StripeExpressBrandingFields
                branding={branding}
                draft={draft}
                isSaving={updateBranding.isPending}
                onDraftChange={(patch) =>
                    setDraft((current) => current && { ...current, ...patch })
                }
                onSave={(nextDraft) => {
                    const updateBody = buildStripeExpressBrandingUpdate({
                        saved: branding,
                        draft: nextDraft,
                    });
                    updateBranding.mutate(updateBody, {
                        onSuccess: (saved) => {
                            setDraft(draftFromStripeExpressBranding(saved));
                            toaster.success("Branding saved");
                        },
                        onError: (error) => {
                            if (!isErrorToastedByBackendClient(error)) {
                                toaster.error("Couldn't save branding");
                            }
                        },
                    });
                }}
                onLogoError={(message) => toaster.error(message)}
            />
        );
    }

    return (
        <section className="account-details__section">
            <header className="account-details__section__header">
                <h4>Branding</h4>
            </header>
            <p className={styles.description}>
                Shown on Stripe Checkout, receipts, and invoices.
            </p>
            {body}
        </section>
    );
};
